import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";

import type { ILegendItem } from "app/components/React/Legends/legendsTypes";

import type { BaseBarItemWithColor } from "../../types";

import { Container, ItemsGroup, TotalGroup, Separator } from "./styledComponents";

interface BaseBarListProps {
    data: BaseBarItemWithColor[];
    legendItems: ILegendItem[];
    renderBarItem: (item: BaseBarItemWithColor) => ReactNode;
    renderTotalItem?: (item: BaseBarItemWithColor) => ReactNode;
    showTotal?: boolean;
}

export const BaseBarList: FC<BaseBarListProps> = ({
    data,
    legendItems,
    renderBarItem,
    renderTotalItem,
    showTotal = true,
}) => {
    const legendMap = useMemo(() => new Map(legendItems.map((lg) => [lg.name, lg])), [legendItems]);

    const { items, total } = useMemo(() => {
        const visibleItems = [];
        let totalItem = null;

        data.forEach((item) => {
            const legend = legendMap.get(item.label);
            const isTotal = item.isTotal === true;
            const visible = isTotal || legend?.visible !== false;
            if (!visible) {
                return;
            }

            const color = isTotal ? colorsPalettes.red[500] : (legend?.color ?? colorsPalettes.carbon[200]);

            if (isTotal) {
                totalItem = { ...item, color };
            } else {
                visibleItems.push({ ...item, color });
            }
        });

        return { items: visibleItems, total: totalItem };
    }, [data, legendMap]);

    return (
        <Container>
            <ItemsGroup>
                {items.map((item) => (
                    <div key={item.label}>{renderBarItem(item)}</div>
                ))}
            </ItemsGroup>
            {showTotal && total && renderTotalItem ? (
                <TotalGroup>
                    <Separator />
                    {renderTotalItem(total)}
                </TotalGroup>
            ) : null}
        </Container>
    );
};
